import { useMemo } from "react";
import styled from "styled-components";
import { Spinner } from "@sablier/v2-components/atoms";
import { Button, Label } from "@sablier/v2-components/molecules";
import { useCheckPlugin } from "@sablier/v2-hooks";
import { useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import { Warning } from "~/client/components/molecules";
import { useAccount } from "~/client/hooks";
import { peripheral } from "~/client/utils";

const WrapperPartial = styled.div`
  flex: 1;
  flex-shrink: 0;
  width: 100%;
`;

const Content = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    gap: ${(props) => props.theme.sizes.edge};
    width: 100%;
  }
`;

const Section = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    gap: ${(props) => props.theme.sizes.edge};
    width: 100%;
    *[data-component="button"] {
      justify-content: flex-start;
    }
  }
`;

const Description = styled.div`
  & > p {
    ${(props) => props.theme.styles.textParagraph}
    & {
      color: ${(props) => props.theme.colors.gray200};
      line-height: 20pt;
    }
  }
`;

const Loader = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    justify-content: center;
    width: 100%;
    height: 300px;
  }
`;

const WarningPlugin = styled(Warning.Plugin)`
  border: 2px solid ${(props) => props.theme.colors.yellow};
  background-color: transparent;
`;

const Wrapper = styled(WrapperPartial)``;

function Plugin() {
  const { t } = useT();
  const { chainId, isConfigured, proxy } = useAccount();

  const isProxyAvailable = useMemo(() => _.isEthereumAddress(proxy), [proxy]);
  const expected = useMemo(
    () => peripheral(chainId, "plugin").address,
    [chainId],
  );

  const registry = useMemo(
    () => peripheral(chainId, "registry").address,
    [chainId],
  );

  const { isAvailable: isAvailablePlugin, isLoading: isLoadingPlugin } =
    useCheckPlugin({
      expected,
      chainId,
      proxy,
      name: "onStreamCanceled",
      registry,
    });

  const isLoading = useMemo(
    () => !isConfigured || isLoadingPlugin,
    [isConfigured, isLoadingPlugin],
  );

  return (
    <Wrapper>
      <Content>
        {isLoading ? (
          <Loader>
            <Spinner color={"white"} />
          </Loader>
        ) : (
          <Section>
            <Label value={t("modalCards.proxy.plugin.title")} />
            <Description>
              <p>{t("modalCards.proxy.plugin.description")}</p>
            </Description>
            {isProxyAvailable ? (
              <>
                {isAvailablePlugin ? (
                  <Button
                    accent={"green"}
                    appearance={"outline"}
                    isDisabled
                    isPreview
                    isMini
                    purpose={"button"}
                    title={t("modalCards.proxy.plugin.installed")}
                  />
                ) : (
                  <WarningPlugin />
                )}
              </>
            ) : (
              <Button
                accent={"dark200"}
                appearance={"solid"}
                isDisabled
                isPreview
                isMini
                purpose={"button"}
                title={t("modalCards.proxy.plugin.missing")}
              />
            )}
          </Section>
        )}
      </Content>
    </Wrapper>
  );
}

export default Plugin;
